import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, watchEffect } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { LineItem } from '@/models/line-item'
import {
  bulkCreateJobLineItemMutation,
  bulkDeleteJobLineItemMutation,
  bulkUpdateJobLineItemMutation,
  getJobLineItemsQuery,
} from '@/api/jobs'
import { v4 } from 'uuid'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { isEqual } from 'lodash'
import VsButton from '@/components/vision/VsButton.vue'
import { AxiosResponse } from 'axios'
import { useStore } from 'vuex-composition-helpers'
import VsText from '@/components/vision/VsText.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import LineItemSelect from '@/components/jobs/line-items/LineItemSelect.vue'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import { getAllPriceBookItems } from '@/api/price-book'
import { fetchLocationById } from '@/api/locations'
import { useQueryClient } from '@tanstack/vue-query'
import Big from 'big.js'

interface Props {
  jobId: string
  assignedToServiceProviderId?: string | null
  hubId: string
  batchId?: string | null
  resourceTypeId: number
  jobDate: Date
  destinationLocationId: number
  pickupLocationIds: number[] | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LineItemForm',
  props: {
    jobId: null,
    assignedToServiceProviderId: { default: null },
    hubId: null,
    batchId: { default: null },
    resourceTypeId: null,
    jobDate: null,
    destinationLocationId: null,
    pickupLocationIds: null
  },
  emits: ["dismiss"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'dismiss'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { jobId: string, assignedToServiceProviderId: string | null, hubId: string, batchId: string | null, resourceTypeId: number, jobDate: Date, destinationLocationId: number, pickupLocationIds: number[] | null }





const store = useStore()

const { data: lineItems, isLoading: isFetching } = getJobLineItemsQuery(
  props.jobId
)
const { data: priceBookItems } = getAllPriceBookItems()

const { isError: isBulkCreateError, mutateAsync: bulkCreateAsync } =
  bulkCreateJobLineItemMutation({
    disableOnSuccessCallBack: true,
  })

const { isError: isBulkUpdateError, mutateAsync: bulkUpdateAsync } =
  bulkUpdateJobLineItemMutation({
    disableOnSuccessCallBack: true,
  })

const { isError: isBulkDeleteError, mutateAsync: bulkDeleteAsync } =
  bulkDeleteJobLineItemMutation({
    disableOnSuccessCallBack: true,
  })

const hasLineItemRateAccessMethod = computed<
  (serviceProviderId: string | null, hubId: string) => boolean
>(() => store.getters['permissions/hasLineItemRateAccess'])
const hasLineItemRateAccess = computed(() =>
  hasLineItemRateAccessMethod.value(
    props.assignedToServiceProviderId,
    props.hubId
  )
)

const formatter = useCurrencyFormatter()

const formData = ref<LineItem[]>()
const form = ref<typeof VsForm | null>(null)
const isSnackbarVisible = ref(false)
const isSaving = ref(false)
const queryClient = useQueryClient()

const destinationLocationTagIds = ref<number[] | null>(null)
const pickupLocationTagIds = ref<number[] | null>(null)

const hasChanges = computed(() => !isEqual(lineItems.value, formData.value))

const lineItemsSubTotal = computed(() => {
  if (!hasLineItemRateAccess.value) return ''
  if (!formData.value) return formatter.format(0)

  const total = formData.value.reduce((sum, { quantity, rate }) => {
    if (!quantity || !rate) return sum

    const val = new Big(quantity).mul(rate)
    const rounded = val.round(2, Big.roundHalfUp)

    return sum.plus(rounded)
  }, new Big(0))

  return formatter.format(Number(total))
})

watch(() => props.destinationLocationId, (newVal) => {
    loadDestinationTags(newVal)
  },
  { immediate: true }
)

watch(() => props.pickupLocationIds, (newVal) => {
    loadPickupTags(newVal)
  },
  { immediate: true }
)

watch(isBulkDeleteError, (isError) => {
  isSnackbarVisible.value = isError
})

watch(isBulkCreateError, (isError) => {
  isSnackbarVisible.value = isError
})

watch(isBulkUpdateError, (isError) => {
  isSnackbarVisible.value = isError
})

watchEffect(() => {
  if (!formData.value && lineItems.value) {
    formData.value = lineItems.value.map(
      (li) =>
        new LineItem({
          id: li.id,
          name: li.name,
          rate: li.rate,
          quantity: li.quantity,
          price_book_item_id: li.priceBookItemId,
          created: li.created,
        })
    )
  }
})

function discard() {
  let confirmed = true

  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?'
    )

    if (!confirmation) {
      confirmed = false
    }
  }

  if (confirmed) {
    emit('dismiss')
  }
}

function dismiss() {
  emit('dismiss')
}

function addLineItem() {
  if (formData.value)
    formData.value.push(
      new LineItem({
        id: `new-${v4()}`,
        name: '',
        rate: null,
        quantity: null,
        price_book_item_id: null,
        created: new Date(),
      })
    )
}

function roundRateInput(value: number | string | null) {
  // Round to 5 decimal places
  if (value == null || value === '') return null
  return Math.abs(
    Math.round(
      (typeof value === 'string' ? parseFloat(value) : value) * 100000
    ) / 100000
  )
}

function roundQuantityInput(value: number | string | null) {
  // Round to 5 significant digits
  if (value == null || value === '') return null
  return Math.abs(
    Math.round(
      (typeof value === 'string' ? parseFloat(value) : value) * 100000
    ) / 100000
  )
}

const quantityRule = (v: string | number) => {
  if (typeof v === 'number') {
    return v <= 0 && 'Quantity must be greater than 0'
  }
  // v now always have the type of string
  return parseFloat(v) <= 0 && 'Quantity must be greater than 0'
}

async function onSubmit() {
  if (
    typeof form.value !== typeof VsForm ||
    !(form.value as typeof VsForm).isValid()
  ) {
    return
  }
  isSaving.value = true

  const actions: Promise<AxiosResponse>[] = []
  const toDelete =
    lineItems.value?.filter(
      (li) => !formData.value?.some((fd) => fd.id === li.id) ?? false
    ) ?? []
  if (toDelete.length > 0) {
    actions.push(
      bulkDeleteAsync({
        jobId: props.jobId,
        lineItemIds: toDelete.map((li) => li.id),
      })
    )
  }

  const toUpdate =
    formData.value?.filter((fd) =>
      lineItems.value?.some(
        (li) =>
          fd.id === li.id &&
          (fd.name !== li.name ||
            fd.rate !== li.rate ||
            fd.quantity !== li.quantity ||
            fd.priceBookItemId !== li.priceBookItemId)
      )
    ) ?? []
  if (toUpdate.length > 0) {
    const bulkUpdateRequest = toUpdate.map((li) => ({
      id: li.id,
      name: li.priceBookItemId !== null ? null : li.name,
      rate: roundRateInput(li.rate),
      quantity: roundQuantityInput(li.quantity),
      price_book_item_id: li.priceBookItemId,
    }))
    actions.push(
      bulkUpdateAsync({ jobId: props.jobId, request: bulkUpdateRequest })
    )
  }

  const toCreate =
    formData.value?.filter((fd) => fd.id.startsWith('new-')) ?? []
  if (toCreate.length > 0) {
    const bulkCreateRequest = toCreate.map((li) => ({
      id: v4(),
      name: li.priceBookItemId !== null ? null : li.name,
      rate: roundRateInput(li.rate),
      quantity: roundQuantityInput(li.quantity),
      price_book_item_id: li.priceBookItemId,
    }))
    actions.push(
      bulkCreateAsync({ jobId: props.jobId, request: bulkCreateRequest })
    )
  }
  try {
    await Promise.all(actions)
    isSaving.value = false

    if (
      !isBulkUpdateError.value ||
      !isBulkCreateError.value ||
      !isBulkDeleteError.value
    ) {
      await Promise.all(
        [
          queryClient.invalidateQueries(['line-items', props.jobId]),
          props.batchId &&
            queryClient.invalidateQueries(['invoice', props.batchId]),
        ].filter(Boolean)
      )
    }

    if (
      !isBulkCreateError.value &&
      !isBulkUpdateError.value &&
      !isBulkDeleteError.value
    ) {
      dismiss()
    }
  } catch (_) {
    isSaving.value = false
  }
}

async function deleteLineItem(lineItem: LineItem) {
  if (formData.value) {
    const index = formData.value.findIndex((li) => li.id === lineItem.id)
    if (index > -1) formData.value.splice(index, 1)
  }
}

async function loadDestinationTags(destinationLocationId) {
  if (!destinationLocationId) return
  const location = await fetchLocationById(queryClient, destinationLocationId)
  destinationLocationTagIds.value = location.tags?.map((tag) => tag.id) || []
}

async function loadPickupTags(pickupLocationIds) {
  if (!pickupLocationIds || pickupLocationIds.length === 0) return

  const locations = await Promise.all(
    pickupLocationIds.map((id) => fetchLocationById(queryClient, id))
  )

  const allTagIds = locations.flatMap(
    (location) => location.tags?.map((tag) => tag.id) || []
  )

  pickupLocationTagIds.value = allTagIds
}

function getLineTotal(lineItem: LineItem) {
  if (!lineItem.quantity || !lineItem.rate) {
    return '-'
  }
  const val = new Big(lineItem.quantity).mul(lineItem.rate)
  const rounded = val.round(2, Big.roundHalfUp)

  return formatter.format(Number(rounded))
}

function getLineItemUnitOfMeasure(lineItem: LineItem) {
  if (!lineItem.priceBookItemId || !priceBookItems.value) return 'Qty'

  return (
    priceBookItems.value.find((item) => item.id === lineItem.priceBookItemId)
      ?.unitOfMeasure ?? 'Qty'
  )
}

function selectPriceBookItem(
  value: {
    id: string | null
    defaultRate: number | null
    name: string
  },
  lineItem: LineItem
) {
  if (value.id == null) {
    lineItem.name = value.name
    lineItem.priceBookItemId = null
  } else {
    lineItem.name = value.name
    lineItem.priceBookItemId = value.id
    lineItem.rate = value.defaultRate
  }
}

return { __sfc: true,props, emit, store, lineItems, isFetching, priceBookItems, isBulkCreateError, bulkCreateAsync, isBulkUpdateError, bulkUpdateAsync, isBulkDeleteError, bulkDeleteAsync, hasLineItemRateAccessMethod, hasLineItemRateAccess, formatter, formData, form, isSnackbarVisible, isSaving, queryClient, destinationLocationTagIds, pickupLocationTagIds, hasChanges, lineItemsSubTotal, discard, dismiss, addLineItem, roundRateInput, roundQuantityInput, quantityRule, onSubmit, deleteLineItem, loadDestinationTags, loadPickupTags, getLineTotal, getLineItemUnitOfMeasure, selectPriceBookItem, FormLayout, VsTextInput, VsWrapper, VsForm, VsSnackbar, VsButton, VsText, VsHeading, LineItemSelect }
}

})